import { Bug, X, Shield, Copy, LogOut, Camera } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { useDeveloperMode } from '@/contexts/DeveloperModeContext';
import { useUser } from '@/contexts/user';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export function DeveloperModeToggle() {
  const { isDevMode, toggleDevMode, isScreenshotMode, toggleScreenshotMode } = useDeveloperMode();
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Hide everything when screenshot mode is active
  if (isScreenshotMode) {
    return null;
  }

  const handleScreenshotMode = () => {
    toggleScreenshotMode();
    toast({
      title: "Screenshot Mode ON",
      description: "Triple-tap bottom-left corner to restore, or wait 30s.",
      duration: 4000,
    });
  };

  const copyUUID = () => {
    navigator.clipboard.writeText(user.id);
    toast({
      title: "UUID Copied",
      description: "User ID copied to clipboard",
    });
  };

  return (
    <>
      {/* Dev Mode Badge */}
      {isDevMode && !isOpen && (
        <div className="fixed bottom-32 right-4 z-[10000] bg-yellow-500 text-black px-2 py-1 rounded-md text-xs font-bold shadow-lg animate-pulse">
          DEV
        </div>
      )}

      {/* Floating Bug Icon Button */}
      <Button
        variant="outline"
        size="icon"
        className="fixed bottom-20 right-4 z-[10000] rounded-full shadow-lg bg-background/95 backdrop-blur-sm"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Bug className={`w-4 h-4 ${isDevMode ? 'text-yellow-500' : 'text-muted-foreground'}`} />
      </Button>

      {/* Developer Panel */}
      {isOpen && (
        <Card className="fixed bottom-32 right-4 z-[10000] w-80 max-h-[70vh] overflow-y-auto shadow-2xl bg-background/95 backdrop-blur-sm">
          <div className="p-4 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between pb-2 border-b">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-yellow-500" />
                <h3 className="font-semibold">Developer Tools</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-6 w-6"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Dev Mode Toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="dev-mode" className="text-sm font-medium">
                Developer Mode
              </Label>
              <Switch
                id="dev-mode"
                checked={isDevMode}
                onCheckedChange={toggleDevMode}
              />
            </div>

            {isDevMode && (
              <>
                {/* Screenshot Mode Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleScreenshotMode}
                  className="w-full gap-2"
                >
                  <Camera className="h-4 w-4" />
                  Screenshot Mode (⌘⇧H)
                </Button>

                {/* Phase Info */}
                <div className="p-2 bg-muted rounded text-xs space-y-1">
                  <p className="font-medium">⚡ Developer Features Active:</p>
                  <p className="text-muted-foreground">• Fast-forward timer buttons visible</p>
                  <p className="text-muted-foreground">• Session data management</p>
                  <p className="text-muted-foreground">• Admin panel access (if admin)</p>
                </div>

                {/* Clear Session Button */}
                <Button
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    localStorage.removeItem('timerSession');
                    sessionStorage.setItem('skipRestore', 'true');
                    window.location.reload();
                  }}
                >
                  🗑️ Clear Saved Session & Reload
                </Button>

                {/* Admin Tools Section */}
                <div className="pt-4 border-t space-y-3">
                  <Label className="text-xs font-medium text-muted-foreground mb-2 block">
                    Admin Tools
                  </Label>

                  {/* Admin Status */}
                  <div className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm font-medium">Admin Status:</span>
                    <span className={`text-sm font-semibold ${
                      user.isAdmin ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'
                    }`}>
                      {user.isAdmin ? '✅ Verified' : '❌ Not Admin'}
                    </span>
                  </div>

                  {/* Admin Panel Link */}
                  {user.isAdmin && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setIsOpen(false);
                        navigate('/admin');
                      }}
                      className="w-full gap-2"
                    >
                      <Shield className="w-4 h-4" />
                      Open Admin Panel
                    </Button>
                  )}

                  {/* UUID Display */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-muted-foreground">Your UUID:</Label>
                    <div className="flex gap-2">
                      <code className="flex-1 text-xs bg-background p-2 rounded border overflow-x-auto break-all">
                        {user.id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyUUID}
                        className="shrink-0"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Info Box */}
                  <div className="p-2 bg-blue-500/10 border border-blue-500/20 rounded text-xs text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">💡 Admin Note:</p>
                    <p>Premium status can be managed via the Admin Panel or directly in Supabase Dashboard (service role has full access).</p>
                  </div>
                </div>
              </>
            )}

            {/* EMERGENCY SIGN OUT - Always visible when panel is open */}
            <div className="pt-4 border-t space-y-2">
              <Label className="text-xs font-medium text-destructive block">
                🚨 Emergency Actions
              </Label>
              <Button
                variant="destructive"
                size="sm"
                className="w-full gap-2"
                onClick={async () => {
                  // Force clear all storage
                  localStorage.clear();
                  sessionStorage.clear();
                  
                  // Sign out from Supabase - guards will handle redirect
                  // V2: Removed window.location.href - per theme-architecture.md
                  try {
                    await supabase.auth.signOut();
                  } catch (e) {
                    console.warn('Signout error (ignored):', e);
                  }
                  // Guards will redirect to /auth after signOut
                }}
              >
                <LogOut className="w-4 h-4" />
                Emergency Sign Out
              </Button>
              <p className="text-xs text-muted-foreground">
                Clears all storage and signs out. Use when stuck.
              </p>
            </div>
          </div>
        </Card>
      )}
    </>
  );
}
